import React, { useEffect, useState } from 'react';
import { useView, ToastList } from '@devesharp/react/web';
import { useParams } from 'react-router-dom';
import { Observable } from 'rxjs';
import {
   CompanyDetailResolve,
   EstablishmentIdezAssociateResolve,
   EstablishmentIdezBiometricsResendResolve,
} from '~/api';
import history from '~/config/history';
import { openBiometricsUrl } from '~/pages/EstablishmentIdez/EstablishmentIdezPage.utils';

function toPromise<T>(observable: Observable<T>): Promise<T> {
   return new Promise((resolve, reject) => {
      observable.subscribe({
         next: resolve,
         error: reject,
      });
   });
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function useEstablishmentDetailPage(): any {
   const { id } = useParams() as any;
   const [resource, setResource] = useState<any>();
   const [resendingEntity, setResendingEntity] = useState<string | null>(null);
   const [showAssociateIdezModal, setShowAssociateIdezModal] = useState(false);
   const [associateIdezId, setAssociateIdezId] = useState('');
   const [associatingIdez, setAssociatingIdez] = useState(false);

   const { starting, errorLoadData, reloadPage, registerResolveParams, registerOnInit } = useView({
      resolves: {
         resource: CompanyDetailResolve,
      },
   });

   registerResolveParams('resource', () => id);

   registerOnInit((res: any) => {
      setResource(res.resource);
      document.title = `${res.resource.name} - XPAG Tecnologia`;
   });

   useEffect(() => {
      if (errorLoadData) {
         ToastList.show({
            message: 'Erro ao carregar estabelecimento',
            type: 'error',
         });
         history.push('/establishment');
      }
   }, [errorLoadData]);

   function openEditCompany() {
      history.push(`/establishment/edit/${id}`);
   }

   function openZoop() {
      history.push(`/establishment/${id}/zoop`);
   }

   function openIdez() {
      history.push(`/establishment/${id}/idez`);
   }

   function backToListing() {
      history.push('/establishment');
   }

   function openAssociateIdezModal() {
      setAssociateIdezId('');
      setShowAssociateIdezModal(true);
   }

   function closeAssociateIdezModal() {
      if (associatingIdez) {
         return;
      }

      setShowAssociateIdezModal(false);
      setAssociateIdezId('');
   }

   async function submitAssociateIdez() {
      const idezAccountId = associateIdezId.trim();

      if (!idezAccountId) {
         ToastList.show({
            message: 'Informe o ID da conta Idez',
            type: 'error',
         });
         return;
      }

      if (!UUID_PATTERN.test(idezAccountId)) {
         ToastList.show({
            message: 'Informe um UUID válido',
            type: 'error',
         });
         return;
      }

      setAssociatingIdez(true);

      try {
         await toPromise(
            EstablishmentIdezAssociateResolve(id, {
               idez_account_id: idezAccountId,
            }),
         );

         ToastList.show({
            message: 'Conta Idez associada com sucesso',
            type: 'success',
         });

         setShowAssociateIdezModal(false);
         setAssociateIdezId('');
         reloadPage();
      } catch (error: any) {
         ToastList.show({
            message: error?.data?.error ?? error?.data?.data?.error ?? 'Erro ao associar conta Idez',
            type: 'error',
         });
      } finally {
         setAssociatingIdez(false);
      }
   }

   function validateKyc(entity: any) {
      if (entity?.biometrics_url) {
         openBiometricsUrl(entity.biometrics_url);
      }
   }

   async function resendKyc(entity: any) {
      setResendingEntity(entity.entity_id);

      try {
         const sessions = await toPromise(
            EstablishmentIdezBiometricsResendResolve(id, {
               entity_document: entity.document ?? undefined,
            }),
         );

         const updated = Array.isArray(sessions)
            ? sessions.find((item: any) => item.entity_id === entity.entity_id) ?? sessions[0]
            : null;

         reloadPage();

         if (updated?.biometrics_url) {
            openBiometricsUrl(updated.biometrics_url);
         }

         ToastList.show({
            message: 'Sessão de biometria renovada',
            type: 'success',
         });
      } catch (error: any) {
         ToastList.show({
            message: error?.data?.error ?? error?.data?.data?.error ?? 'Erro ao renovar sessão',
            type: 'error',
         });
      } finally {
         setResendingEntity(null);
      }
   }

   return {
      id,
      resource,
      starting,
      errorLoadData,
      reloadPage,
      openEditCompany,
      openZoop,
      openIdez,
      backToListing,
      validateKyc,
      resendKyc,
      resendingEntity,
      showAssociateIdezModal,
      associateIdezId,
      setAssociateIdezId,
      associatingIdez,
      openAssociateIdezModal,
      closeAssociateIdezModal,
      submitAssociateIdez,
   };
}
